// © Microsoft Corporation. All rights reserved.

import { accentFillRest, DesignSystem, isDarkMode, neutralFillActive, neutralForegroundHint, neutralForegroundRest } from "@microsoft/fast-components-styles-msft";
import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";
import {
    avatarPadding,
    commentVPadding, enums, highContrastButton,
    mainFontSize,
    mainLineHeight
} from "@msnews/social-utils";
import { mainPadding as mainCommentPadding } from "../Comment.styles";

const messageMaxClampedLines = 4;

export const CommentItemStyles = (config: DesignSystem) => ({
    commentItemContainer: {
        position: "relative",
        width: "100%",
        padding: commentVPadding + " 0",
        border: "none",
        "border-top": "1px solid #DDDDDD",
        "border-color": neutralFillActive,

        "&:first-child": {
            border: "none"
        },

        "& .cwt-comment-box-container": {
            margin: commentVPadding + " 0 0"
        },

        "& .cwt-comment-content": {
            margin: "0",
            width: "inherit",

            "& .cwt-avatar": {
                float: applyLocalizedProperty("left", "right", config.direction),
                position: "relative",
                "z-index": "2"
            },
            "& .cwt-avatar-link.cwt-link": {
                "&:focus, &:focus-visible": {
                    position: "absolute"
                }
            },

            "& .cwt-inner-content": {
                [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: avatarPadding
            },

            "& .cwt-header": {
                margin: "0",
                padding: "0",

                "& .dot-before::before": {
                    opacity: ".8"
                },
                "& .cwt-posted-at": {
                    color: neutralForegroundHint,
                    "font-size": mainFontSize
                }
            },
            "& .cwt-name": {
                "font-size": mainFontSize,
                "font-weight": "600",
                color: neutralForegroundRest,

                "&.cwt-link": {
                    "line-height": mainLineHeight,
                    height: mainLineHeight,
                    display: "inline-flex",
                    overflow: "hidden",
                    "text-overflow": "ellipsis",
                    color: isDarkMode(config) ? "#5CB6FF" : accentFillRest
                }
            },
            "& .cwt-deleted-name": {
                "font-weight": "600"
            },
            "& .cwt-message": {
                padding: "0",
                margin: "3px 0 2px",
                "font-size": "16px",
                "line-height": mainLineHeight,
                background: "none",
                border: "none",
                "word-break": "break-word",

                "& .cwt-name.cwt-link": {
                    "font-weight": "normal",
                    display: "inline",

                    "&:before": {
                        content: "'@'"
                    }
                },

                "& .cwt-comment-deleted-message": {
                    display: "inline",
                    "font-style": "italic",
                    color: neutralForegroundHint,

                    "& .cwt-icon": {
                        margin: "-4px 4px 0",
                        color: neutralForegroundHint,
                        fill: neutralForegroundHint,
                        display: "inline-flex",
                        "vertical-align": "middle"
                    }
                },

                "& > .cwt-clamp": {
                    display: "-webkit-box",
                    "-webkit-line-clamp": messageMaxClampedLines,
                    "-webkit-box-orient": "vertical",
                    overflow: "hidden"
                },

                "& .cwt-show-clamped-btn": {
                    color: isDarkMode(config) ? "#5CB6FF" : accentFillRest,
                    "font-size": "12px"
                }
            },
            "& .cwt-footer": {
                position: "relative",
                display: "flex",
                "align-items": "center",
                "min-height": "20px",
                "font-size": "12px",
                margin: "11px 0 0",
                color: "black",

                "& .cwt-thread-locked-message": {
                    "letter-spacing": applyLocalizedProperty("-.25pt", "0", config.direction),
                    fill: "#09664f"
                },

                "& .cwt-report-btn": {
                    "font-size": "16px",
                    [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "auto",
                    color: neutralForegroundHint,
                    fill: "currentColor",

                    '&[data-is-active="true"]': {
                        color: accentFillRest
                    },

                    "&:hover, &:focus ": {
                        color: "#5CB6FF",
                        "& .cwt-icon-label": {
                            color: "#5CB6FF"
                        },
                        ...highContrastButton
                    },

                    "& .cwt-icon-label": {
                        "font-size": "13px",
                        color: neutralForegroundHint
                    }
                }
            }
        },

        "&&.cwt-specific-comment": {
            "background-color": isDarkMode(config) ? "#323232" : "#F7F7F7",
            [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: `calc(0px - ${mainCommentPadding}px)`,
            "padding-right": `${mainCommentPadding}px`,
            "padding-left": `${mainCommentPadding}px`,
            "&.cwt-specific-reply": {
                [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: `calc(0px - (${avatarPadding} + ${mainCommentPadding}px))`,
                [applyLocalizedProperty("padding-left", "padding-right", config.direction)]: `calc(${avatarPadding} + ${mainCommentPadding}px)`
            }
        },

        "&:last-child": {
            "& .cwt-comment:last-child": {
                border: "none",
                "padding-bottom": "0"
            }
        }
    },

    commentItemDeleteMenuPopover: {
        bottom: "16px",
        [applyLocalizedProperty("right", "left", config.direction)]: "0"
    },

    commentItemFooterTwoLines: {
        "& .cwt-comment-content": {
            "& .cwt-footer": {
                "flex-wrap": "wrap",

                "& .cwt-reaction-counts": {
                    padding: "0 0 11px 0"
                },

                "& .cwt-report-btn": {
                    "& .cwt-icon-label": {
                        display: "none"
                    }
                },
                "& .cwt-line-break": {
                    "flex-basis": "100%",
                    width: "0",
                    height: "0",
                    overflow: "hidden"
                }
            }
        }
    },

    commentItemOverlayContainer: {
        [`&.${enums.responsiveLevels.MobilePopup}`]: {
            border: "none"
        }
    }
});